/**
 * 无限画布 · 模型列表与选模扣费
 * GET /modelslist — 上架模型定价
 * POST /models/charge — 可选：显式扣费（画布选模不调用，执行时在业务接口扣费）
 */

import api from '@/utils/api'
import { fetchCreditsUserProfile } from '@/utils/creditsRechargeApi'
import { getApiErrorMessage } from '@/utils/vipSkuApi'
import {
  NGC_AUDIO_MODEL_OPTIONS,
  NGC_IMAGE_MODEL_OPTIONS,
  NGC_LLM_MODEL_OPTIONS,
  NGC_SEEDANCE_BACKEND_DEFAULT,
  NGC_VIDEO_MODEL_OPTIONS
} from '@/constants/nodeGraphCanvasModelOptions'
import {
  DEFAULT_VIDEO_RESOLUTION_CREDITS,
  NGC_VIDEO_RESOLUTION_LABELS,
  NGC_VIDEO_RESOLUTIONS,
  normalizeVideoResolution
} from '@/constants/videoResolutionPricing'

export const NGC_MODEL_KIND = {
  LLM: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio'
}

export const MODEL_PRICING_MODE = {
  FLAT: 'flat',
  RESOLUTION: 'resolution'
}

/** @returns {Record<string, number>|null} */
export function parseResolutionPricesJson(raw) {
  if (raw == null || raw === '') return null
  let obj = raw
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null
  const out = {}
  for (const key of NGC_VIDEO_RESOLUTIONS) {
    const v = obj[key] ?? obj[key.toUpperCase()]
    if (v != null && v !== '') out[key] = Math.max(0, Math.round(Number(v) || 0))
  }
  return Object.keys(out).length ? out : null
}

export function isResolutionPricingMode(row) {
  if (!row || typeof row !== 'object') return false
  const mode = String(row.pricingMode ?? row.pricing_mode ?? MODEL_PRICING_MODE.FLAT)
    .trim()
    .toLowerCase()
  if (mode === MODEL_PRICING_MODE.RESOLUTION) return true
  return !!parseResolutionPricesJson(
    row.resolutionPrices ?? row.resolution_prices_json
  )
}

/** 视频：按清晰度取每秒积分；非分级则回退 credits */
export function resolveVideoCreditsPerSecond(hit, resolution) {
  if (!hit) return 0
  const res = normalizeVideoResolution(resolution)
  if (isResolutionPricingMode(hit)) {
    const tiers =
      hit.resolutionPrices ||
      parseResolutionPricesJson(hit.resolution_prices_json)
    if (tiers && tiers[res] != null) return Number(tiers[res]) || 0
  }
  return Number(hit.credits) || 0
}

function assertApiOk(res) {
  if (res == null || typeof res !== 'object') return
  const c = res.code
  if (c === undefined || c === null) return
  const n = Number(c)
  if (n === 0 || n === 200) return
  const msg = res.message || res.msg || res.error || `接口 code=${c}`
  throw new Error(msg)
}

/**
 * 兼容 Dingo/网关多层包装：data.list、data.data.list、result、items 等
 */
function unwrapList(res) {
  if (res == null) return []
  if (typeof res === 'string') {
    try {
      return unwrapList(JSON.parse(res))
    } catch {
      return []
    }
  }
  if (!res || typeof res !== 'object') return []
  if (Array.isArray(res)) return res
  if (Array.isArray(res.list)) return res.list
  if (Array.isArray(res.result)) return res.result
  if (Array.isArray(res.records)) return res.records
  if (Array.isArray(res.items)) return res.items

  const d = res.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    if (Array.isArray(d.list)) return d.list
    if (Array.isArray(d.records)) return d.records
    if (Array.isArray(d.items)) return d.items
    if (Array.isArray(d.data)) return d.data
    const inner = d.data
    if (inner && typeof inner === 'object') {
      if (Array.isArray(inner.list)) return inner.list
      if (Array.isArray(inner.records)) return inner.records
      if (Array.isArray(inner.items)) return inner.items
      if (Array.isArray(inner.data)) return inner.data
    }
  }
  return []
}

/** 解包 POST/GET 业务 data（兼容双层 data） */
function unwrapPayload(res) {
  if (res == null || typeof res !== 'object') return {}
  let d = res.data != null && typeof res.data === 'object' ? res.data : res
  for (let i = 0; i < 4; i++) {
    if (!d || typeof d !== 'object' || Array.isArray(d)) break
    if (
      d.data != null &&
      typeof d.data === 'object' &&
      !Array.isArray(d.data) &&
      !('credits' in d) &&
      !('charged' in d) &&
      !('model_id' in d) &&
      !('score_after' in d)
    ) {
      d = d.data
      continue
    }
    break
  }
  return d && typeof d === 'object' ? d : {}
}

/** @returns {string} */
export function normalizeModelsListKind(raw) {
  const k = String(raw || '')
    .trim()
    .toLowerCase()
  if (k === 'text' || k === 'llm') return NGC_MODEL_KIND.LLM
  if (k === 'image') return NGC_MODEL_KIND.IMAGE
  if (k === 'video') return NGC_MODEL_KIND.VIDEO
  if (k === 'audio') return NGC_MODEL_KIND.AUDIO
  if (k === 'agent') return NGC_MODEL_KIND.LLM
  return k || NGC_MODEL_KIND.IMAGE
}

/**
 * 单条 models 表记录 → 画布下拉项
 * @param {object} row
 */
export function mapModelsListRow(row) {
  if (!row || typeof row !== 'object') return null
  const id = String(
    row.model_id || row.modelId || row.model || row.value || ''
  ).trim()
  if (!id) return null
  const credits =
    Number(
      row.credits ??
        row.credit ??
        row.score ??
        row.score_price ??
        row.price ??
        0
    ) || 0
  const pricingMode = String(row.pricing_mode || row.pricingMode || MODEL_PRICING_MODE.FLAT)
    .trim()
    .toLowerCase()
  const resolutionPrices = parseResolutionPricesJson(
    row.resolution_prices_json ?? row.resolutionPrices
  )
  return {
    id,
    label: String(row.title || row.name || id),
    kind: normalizeModelsListKind(row.kind),
    credits,
    priceUnit: String(row.price_unit || row.unit || '次'),
    pricingMode:
      pricingMode === MODEL_PRICING_MODE.RESOLUTION || resolutionPrices
        ? MODEL_PRICING_MODE.RESOLUTION
        : MODEL_PRICING_MODE.FLAT,
    resolutionPrices,
    thumb: row.thumb || null,
    uuid: row.uuid || null
  }
}

/**
 * GET /modelslist
 * @param {{ kind?: string }} [opts]
 */
export async function fetchModelsList(opts = {}) {
  const params = {}
  const kind = opts.kind != null ? String(opts.kind).trim() : ''
  if (kind) params.kind = kind
  const res = await api.get('/modelslist', { params })
  assertApiOk(res)
  const list = unwrapList(res)
  return list.map(mapModelsListRow).filter(Boolean)
}

/**
 * POST /models/charge — 前端选择模型时扣费
 * @param {{ modelId: string, nodeType?: string, nodeId?: string, scene?: string, duration?: number, resolution?: string }} payload
 */
export async function chargeModelSelection(payload = {}) {
  const modelId = String(payload.modelId || payload.model_id || '').trim()
  if (!modelId) throw new Error('缺少 model_id')
  const body = {
    model_id: modelId,
    node_type: payload.nodeType != null ? String(payload.nodeType) : undefined,
    node_id: payload.nodeId != null ? String(payload.nodeId) : undefined,
    scene: payload.scene != null ? String(payload.scene) : 'node_graph_canvas'
  }
  if (payload.duration != null) body.duration = Math.max(1, Number(payload.duration) || 5)
  if (payload.resolution != null) {
    body.resolution = normalizeVideoResolution(payload.resolution)
  }
  const res = await api.post('/models/charge', body)
  assertApiOk(res)
  const d = unwrapPayload(res)
  return {
    modelId,
    credits: Number(d.credits ?? d.charged ?? 0) || 0,
    scoreAfter: d.score_after != null ? Number(d.score_after) : d.scoreAfter,
    skipped: !!(d.skipped || d.charged === 0)
  }
}

const FALLBACK_BY_KIND = {
  [NGC_MODEL_KIND.LLM]: NGC_LLM_MODEL_OPTIONS,
  [NGC_MODEL_KIND.IMAGE]: NGC_IMAGE_MODEL_OPTIONS,
  [NGC_MODEL_KIND.VIDEO]: NGC_VIDEO_MODEL_OPTIONS,
  [NGC_MODEL_KIND.AUDIO]: NGC_AUDIO_MODEL_OPTIONS
}

/**
 * 画布下拉：仅展示 GET /modelslist 中已上架的模型（不合并本地未配置项）
 * @param {object[]} catalog
 * @param {string} kind
 */
export function mergeCanvasModelOptions(catalog, kind) {
  const k = normalizeModelsListKind(kind)
  const fallbacks = FALLBACK_BY_KIND[k] || []
  const labelById = new Map()
  for (const fb of fallbacks) {
    const rawId = fb && fb.id != null ? String(fb.id) : ''
    const id =
      rawId.trim() ||
      (fb && fb.backendDefault ? String(fb.backendDefault).trim() : '')
    if (id && fb && fb.label) labelById.set(id, String(fb.label))
  }

  const out = []
  for (const row of catalog || []) {
    if (!row || row.kind !== k) continue
    const item = { ...row }
    const fbLabel = labelById.get(String(item.id))
    if (fbLabel && (!item.label || item.label === item.id)) {
      item.label = fbLabel
    }
    out.push(item)
  }
  return out
}

/** 执行拦截：模型已下架（与后台 status=0 未出现在 /modelslist 一致） */
export const MODEL_OFF_SHELF_MESSAGE = '模型已下架'

/**
 * 解析节点执行时将校验的 model_id（与 ngcRequiredCreditsForRun 对齐）
 * @returns {{ kind: string, modelId: string }[]}
 */
export function collectNodeRunModelTargets(n, opts = {}) {
  if (!n || !n.data) return []
  const t = n.type
  if (t === 'image') {
    if (String(n.data.imageInputMode || '') === 'upload') return []
    const model = String(n.data.seedreamModel || '').trim()
    if (!model) return [{ kind: NGC_MODEL_KIND.IMAGE, modelId: '' }]
    const kind =
      model === 'character-designer' ? NGC_MODEL_KIND.LLM : NGC_MODEL_KIND.IMAGE
    return [{ kind, modelId: model }]
  }
  if (t === 'video') {
    return [
      {
        kind: NGC_MODEL_KIND.VIDEO,
        modelId: String(n.data.seedanceModel != null ? n.data.seedanceModel : '')
      }
    ]
  }
  if (t === 'animateMove') {
    return [
      {
        kind: NGC_MODEL_KIND.VIDEO,
        modelId: String(n.data.model || 'wan2.2-animate-move').trim() || 'wan2.2-animate-move'
      }
    ]
  }
  if (t === 'llm' || t === 'storyboard') {
    const model = String(n.data.model || '').trim()
    if (!model) return [{ kind: NGC_MODEL_KIND.LLM, modelId: '' }]
    return [{ kind: NGC_MODEL_KIND.LLM, modelId: model }]
  }
  if (t === 'audio') {
    if (String(n.data.audioInputMode || '') === 'upload') return []
    const model = String(n.data.model || '').trim()
    if (!model) return [{ kind: NGC_MODEL_KIND.AUDIO, modelId: '' }]
    return [{ kind: NGC_MODEL_KIND.AUDIO, modelId: model }]
  }
  if (t === 'ecomDetail') {
    const out = []
    if (opts.renderImages) {
      const model = String(n.data.seedreamModel || '').trim()
      if (model) out.push({ kind: NGC_MODEL_KIND.IMAGE, modelId: model })
    } else {
      const ds = String(n.data.deepseekModel || '').trim()
      if (ds) out.push({ kind: NGC_MODEL_KIND.LLM, modelId: ds })
    }
    return out
  }
  if (t === 'uiDesign') {
    const out = []
    if (opts.renderImages) {
      const model = String(n.data.seedreamModel || '').trim()
      if (model) out.push({ kind: NGC_MODEL_KIND.IMAGE, modelId: model })
    } else {
      const ds = String(n.data.deepseekModel || '').trim()
      if (ds) out.push({ kind: NGC_MODEL_KIND.LLM, modelId: ds })
    }
    return out
  }
  return []
}

/**
 * 执行前校验：模型须在 /modelslist（status=1 上架）中
 */
export function assertModelsOnShelf(catalog, targets) {
  const list = Array.isArray(targets) ? targets : []
  if (!list.length) return
  const cat = Array.isArray(catalog) ? catalog : []
  if (!cat.length) {
    throw new Error(MODEL_OFF_SHELF_MESSAGE)
  }
  for (let i = 0; i < list.length; i++) {
    const kind = list[i].kind
    const raw = String(list[i].modelId != null ? list[i].modelId : '').trim()
    const chargeId =
      normalizeModelsListKind(kind) === NGC_MODEL_KIND.VIDEO
        ? resolveVideoModelChargeId(raw)
        : raw
    if (!chargeId) {
      throw new Error(MODEL_OFF_SHELF_MESSAGE)
    }
    if (!isModelInCatalog(cat, chargeId, kind)) {
      throw new Error(MODEL_OFF_SHELF_MESSAGE)
    }
  }
}

/** 当前 model_id 是否在接口定价列表中 */
export function isModelInCatalog(catalog, modelId, kind) {
  const k = normalizeModelsListKind(kind)
  const raw = String(modelId != null ? modelId : '').trim()
  const chargeId =
    k === NGC_MODEL_KIND.VIDEO ? resolveVideoModelChargeId(raw) : raw
  return !!findModelInCatalog(catalog, chargeId, kind)
}

/** 视频节点空 id 时用于匹配定价的后端默认 model_id */
export function resolveVideoModelChargeId(modelId) {
  const id = String(modelId != null ? modelId : '').trim()
  return id || NGC_SEEDANCE_BACKEND_DEFAULT
}

/**
 * 根据定价表计算单次执行所需积分
 * @param {object[]} catalog
 * @param {string} modelId
 * @param {string} kind
 * @param {{ times?: number, duration?: number, resolution?: string }} [opts]
 */
export function calcRequiredCreditsFromCatalog(catalog, modelId, kind, opts = {}) {
  const hit = findModelInCatalog(catalog, modelId, kind)
  if (!hit) return 0
  const times = Math.max(1, Number(opts.times) || 1)
  const duration = Math.max(1, Number(opts.duration) || 5)
  const unit = String(hit.priceUnit || '次')
  const k = normalizeModelsListKind(kind)

  if (unit.includes('张')) {
    const credits = Number(hit.credits) || 0
    if (credits <= 0) return 0
    return credits * times
  }
  if (unit.includes('秒')) {
    const perSec =
      k === NGC_MODEL_KIND.VIDEO
        ? resolveVideoCreditsPerSecond(hit, opts.resolution)
        : Number(hit.credits) || 0
    if (perSec <= 0) return 0
    return perSec * duration
  }
  const credits = Number(hit.credits) || 0
  if (credits <= 0) return 0
  return credits * times
}

/**
 * 执行模型前校验用户积分（不足则 throw）
 * @param {{ required: number, label?: string }} opts
 */
export async function assertUserHasCredits(opts = {}) {
  const required = Math.max(0, Number(opts.required) || 0)
  if (required <= 0) return { score: 0, required: 0 }
  const profile = await fetchCreditsUserProfile()
  const score = Number(profile.score) || 0
  if (score < required) {
    const label = opts.label ? String(opts.label) : '本次操作'
    throw new Error('积分不足~')
  }
  return { score, required }
}

export function findModelInCatalog(catalog, modelId, kind) {
  const k = normalizeModelsListKind(kind)
  const id = String(modelId || '').trim()
  const list = Array.isArray(catalog) ? catalog : []
  if (!id) {
    if (k === NGC_MODEL_KIND.VIDEO) {
      return (
        list.find(
          m => m && m.kind === k && String(m.id) === NGC_SEEDANCE_BACKEND_DEFAULT
        ) || null
      )
    }
    return null
  }
  let hit = list.find(m => m && m.kind === k && String(m.id) === id)
  if (!hit) hit = list.find(m => m && String(m.id) === id)
  return hit || null
}

/** 接口定价：单次基础积分（不含张数/时长乘数） */
export function catalogBaseCredits(catalog, modelId, kind) {
  const hit = findModelInCatalog(catalog, modelId, kind)
  if (!hit) return 0
  return Number(hit.credits) || 0
}

/** 下拉项旁「x 积分/单位」文案，仅来自 catalog */
export function catalogCreditsLine(catalog, modelId, kind, opts = {}) {
  const hit = findModelInCatalog(catalog, modelId, kind)
  if (!hit) return ''
  const unit = String(hit.priceUnit || '次')
  const k = normalizeModelsListKind(kind)

  if (k === NGC_MODEL_KIND.VIDEO && isResolutionPricingMode(hit)) {
    const tiers =
      hit.resolutionPrices ||
      parseResolutionPricesJson(hit.resolution_prices_json) ||
      DEFAULT_VIDEO_RESOLUTION_CREDITS
    const res = normalizeVideoResolution(opts.resolution)
    const cur = resolveVideoCreditsPerSecond(hit, res)
    const parts = NGC_VIDEO_RESOLUTIONS.map(r => {
      const label = NGC_VIDEO_RESOLUTION_LABELS[r] || r
      const v = tiers[r] != null ? tiers[r] : hit.credits
      return `${label} ${v}`
    })
    if (cur > 0 && unit.includes('秒')) {
      return `${cur} 积分/秒（${normalizeVideoResolution(opts.resolution)}）· ${parts.join(' / ')}`
    }
    return parts.map(p => `${p}/秒`).join(' · ')
  }

  const c = Number(hit.credits) || 0
  if (c <= 0) return ''
  return `${c} 积分/${unit}`
}

export { getApiErrorMessage }
